import { plainText, roleOf, type NodeRecord, type Scene } from '@flobi/artwork-core';
import { ARTBOARD_PRESETS } from './artboardPresets';

/**
 * One artboard said in a few plain lines, for the questions Jev answers about a page (Animate's
 * suggested recipe, Export's format, the Templates panel's category; ai/pageSmarts.ts). What is on
 * it by role (the engine's own `roleOf`, the one animation uses), its words, its size and what the
 * file is called — never ids, never a picture. Null when there is no such artboard.
 */
export interface PageFacts { pictures: number; texts: number; shapes: number; backdrop: boolean; words: number; print: boolean }

export function pageFacts(scene: Pick<Scene, 'get' | 'childrenOf'>, boardId: string): PageFacts | null {
  const board = scene.get(boardId) as (NodeRecord & { width?: number; height?: number }) | undefined;
  if (!board || board.type !== 'artboard') return null;
  const frame = { width: board.width ?? 0, height: board.height ?? 0 };
  const facts: PageFacts = { pictures: 0, texts: 0, shapes: 0, backdrop: false, words: 0, print: false };
  for (const id of scene.childrenOf(boardId)) {
    const node = scene.get(id);
    if (!node || (node as { visible?: boolean }).visible === false) continue;
    const role = roleOf(node, frame, scene as Scene);
    if (role === 'backdrop') facts.backdrop = true;
    else if (role === 'image') facts.pictures++;
    else if (role === 'text') facts.texts++;
    else facts.shapes++;
    if (node.type === 'text') facts.words += plainText(node).split(/\s+/).filter(Boolean).length;
  }
  const preset = ARTBOARD_PRESETS.find((p) => p.id === (board as { preset?: string }).preset);
  facts.print = preset?.group === 'Paper' || preset?.group === 'Print';
  return facts;
}

export function pageSummary(scene: Pick<Scene, 'get' | 'childrenOf'>, boardId: string, o: { fileName?: string | null; moves?: boolean } = {}): string | null {
  const board = scene.get(boardId) as (NodeRecord & { width?: number; height?: number; name?: string; preset?: string }) | undefined;
  const facts = pageFacts(scene, boardId);
  if (!board || !facts) return null;
  const preset = ARTBOARD_PRESETS.find((p) => p.id === board.preset);
  const lines: string[] = [];
  const name = (o.fileName ?? '').trim();
  if (name && !/^untitled$/i.test(name)) lines.push(`The file is called "${name.slice(0, 120)}".`);
  lines.push(preset ? `The page is a ${preset.name} (${preset.group}, ${preset.says}).` : `The page is ${Math.round(board.width ?? 0)} × ${Math.round(board.height ?? 0)}.`);
  const parts = [
    facts.backdrop && 'a picture or colour covering the whole page',
    facts.pictures && `${facts.pictures} ${facts.pictures === 1 ? 'picture' : 'pictures'}`,
    facts.texts && `${facts.texts} ${facts.texts === 1 ? 'text' : 'texts'} (${facts.words} words)`,
    facts.shapes && `${facts.shapes} ${facts.shapes === 1 ? 'shape or graphic' : 'shapes or graphics'}`,
  ].filter(Boolean);
  lines.push(parts.length ? `On it: ${parts.join(', ')}.` : 'It is empty.');
  const words = scene
    .childrenOf(boardId)
    .map((id) => scene.get(id))
    .filter((node): node is NodeRecord => !!node && node.type === 'text')
    .map((node) => plainText(node).replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' / ');
  if (words) lines.push(`Its words: "${words.slice(0, 240)}${words.length > 240 ? '…' : ''}"`);
  if (o.moves) lines.push('It has animation: it moves.');
  return lines.join('\n');
}
